import { useQuery } from '@tanstack/react-query'
import { CreditCard, Download } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { APIResponse, CardPromoClaimReport } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'

interface Props {
  range: UseReportRange
}

/**
 * Card Promos tab — the per-bank reimbursement claim report. Bank card
 * promo discounts are fronted by the restaurant and claimed back from the
 * issuing bank, so this tab is built to be handed to the bank: one card
 * per bank, order-level rows, refunded orders flagged and excluded from
 * every total. CSV export is client-side from the loaded rows.
 */
export function CardPromosTab({ range }: Props) {
  const { formatCurrency } = useCurrency()

  const query = useQuery<APIResponse<CardPromoClaimReport>>({
    queryKey: ['reports-card-promos', range.fromISO, range.toISO],
    queryFn: () => apiClient.getCardPromoReport(range.fromISO, range.toISO),
    staleTime: 60_000,
  })

  const report = query.data?.data

  function exportCsv() {
    if (!report) return
    const lines: string[] = [
      ['Bank', 'Card', 'Business date', 'Order #', 'Order total', 'Discount %', 'Discount (PKR)', 'Status'].join(','),
    ]
    for (const bank of report.banks) {
      for (const row of bank.rows) {
        lines.push(
          [
            csvCell(bank.bank_name),
            csvCell(row.card_label),
            csvCell(row.business_date_label),
            csvCell(row.order_number),
            String(row.order_total),
            String(row.discount_percent),
            String(row.discount_applied),
            row.claimable ? 'claimable' : (row.excluded_reason ?? 'excluded'),
          ].join(','),
        )
      }
    }
    lines.push('')
    lines.push(`Total claimable,,,,${''},,${report.grand_total},`)
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `card-promo-claims_${range.fromISO}_${range.toISO}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!report || report.banks.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CreditCard className="mx-auto mb-3 h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            No card-promo redemptions in this period. Redemptions appear here as cashiers apply
            bank card promos at checkout (set them up under Bank Card Promos).
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {report.from_label} → {report.to_label} ·{' '}
          <span className="font-medium text-foreground">
            {report.total_orders.toLocaleString()} claimable order{report.total_orders === 1 ? '' : 's'}
          </span>{' '}
          · total to claim{' '}
          <span className="font-semibold tabular-nums text-foreground">
            {formatCurrency(report.grand_total)}
          </span>
          {report.excluded_count > 0 && (
            <> · {report.excluded_count} excluded (refunded / not completed)</>
          )}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={exportCsv} className="gap-1.5">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {report.banks.map((bank) => (
        <Card key={bank.bank_name}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden />
              {bank.bank_name}
              <Badge variant="outline" className="ml-1 text-sm tabular-nums">
                {bank.order_count}
              </Badge>
            </CardTitle>
            <div className="text-sm">
              Claim from {bank.bank_name}:{' '}
              <span className="font-semibold tabular-nums">{formatCurrency(bank.claim_total)}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-transparent hover:bg-transparent">
                  <TableHead className="w-[14%]">Date</TableHead>
                  <TableHead className="w-[16%]">Order #</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead className="w-[10%] text-right">%</TableHead>
                  <TableHead className="w-[14%] text-right">Order total</TableHead>
                  <TableHead className="w-[14%] text-right">Discount</TableHead>
                  <TableHead className="w-[12%] text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bank.rows.map((row) => (
                  <TableRow key={row.order_id} className={row.claimable ? '' : 'opacity-60'}>
                    <TableCell className="text-sm tabular-nums">{row.business_date_label}</TableCell>
                    <TableCell className="font-mono text-sm">{row.order_number}</TableCell>
                    <TableCell className="text-sm">{row.card_label}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.discount_percent}%
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {formatCurrency(row.order_total)}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium tabular-nums">
                      {formatCurrency(row.discount_applied)}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.claimable ? (
                        <Badge variant="outline" className="text-sm">Claimable</Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="border border-amber-400/50 bg-amber-50 text-sm text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                        >
                          {row.excluded_reason === 'refunded' ? 'Refunded' : 'Not completed'}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function csvCell(v: string): string {
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`
  }
  return v
}
